"use client";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import Image from "next/image";
import { Link } from "@/navigation";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";
import GetInstantQuoteButton from "@/components/NewVersion/GetInstantQuoteButton";
import { CloudArrowUp, LockSimple } from "@phosphor-icons/react";

// ─── 类型定义 ───

export interface BannerStat {
	value: string;
	label: string;
}

// 右侧内容类型：上传卡片 或 自定义内容
export type RightContentType = "upload" | "image" | "custom";

export interface BannerProps {
	/** 主标题上方徽章文案（如 Trustpilot 评分），可选 */
	topBadge?: string;
	/** 主标题 */
	title: string;
	/** 描述段落 */
	description: string;
	/** 特性列表（带勾选图标），可选 */
	features?: string[];
	/** 是否将 feature 中冒号前的文字加粗，默认 false */
	featureBoldColon?: boolean;
	/** 右侧内容类型：upload（默认上传卡片）、image（单张图片）、custom（自定义） */
	rightContentType?: RightContentType;
	/** 右侧自定义内容（当 rightContentType 为 custom 时使用） */
	rightContent?: ReactNode;
	/** 右侧单张图片路径（当 rightContentType 为 image 时使用） */
	rightImage?: string;
	/** 右侧图片 alt 文字 */
	rightImageAlt?: string;
	/** 轮播图片路径数组（当 rightContentType 为 upload 时使用） */
	carouselImages?: string[];
	/** 每组可见图片数，默认 3 */
	visibleCount?: number;
	/** 右侧卡片 - 上传区标题 */
	uploadTitle?: string;
	/** 右侧卡片 - 上传区副标题 */
	uploadSubtitle?: string;
	/** 右侧卡片 - 支持格式文字 */
	uploadFormats?: string;
	/** 右侧卡片 - 安全提示 */
	uploadSecure?: string;
	/** 右侧卡片 - 文件大小限制文字 */
	fileSize?: string;
	/** 右侧卡片 - 底部信任文案 */
	trustedBy?: string;
	/** 右侧按钮文案（悬浮报价表单按钮） */
	productionQuoteText?: string;
	/** 左侧第一个按钮文案（跳转到 /upload 或 primaryButtonHref），可选 */
	primaryButtonText?: string;
	/** 左侧第一个按钮链接；若以 http 开头则用外链并新窗口打开，否则用 Link */
	primaryButtonHref?: string;
	/** 右侧上传卡片内统计项（value + label），可选 */
	stats?: BannerStat[];
	/** 左侧第二个按钮文案（打开悬浮表单），可选 */
	secondaryButtonText?: string;
	/** 轮播图片自定义宽度，默认 100px（移动端）/ 120px（桌面端） */
	carouselImageWidth?: number;
	/** 轮播图片自定义高度，默认 100px（移动端）/ 120px（桌面端） */
	carouselImageHeight?: number;
	/** 轮播图片各自的缩放比例，例如 [1, 1, 1.5] 表示第三张放大1.5倍 */
	carouselImageScales?: number[];
}

// ─── 内部子组件 ───

const UploadIcon = ({ className }: { className?: string }) => (
	<svg viewBox="12 14 40 32" className={className}>
		<path
			fill="#0F7E03"
			d="M15,44V15c0-0.55,0.45-1,1-1h7.73c0.25,0,0.5,0.1,0.68,0.27l4.26,3.99c0.19,0.17,0.43,0.27,0.68,0.27H48c0.55,0,1,0.45,1,1V44c0,0.55-0.45,1-1,1H16C15.45,45,15,44.55,15,44z"
		/>
		<path
			fill="#65A525"
			d="M12.18,27.16c-0.1-0.61,0.37-1.16,0.99-1.16h37.66c0.61,0,1.08,0.55,0.99,1.16l-2.68,17c-0.08,0.49-0.5,0.84-0.99,0.84H15.85c-0.49,0-0.91-0.36-0.99-0.84L12.18,27.16z"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		className="h-5 w-5 flex-shrink-0 text-themePrimary400"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M4 10 Q 7 14 9 12 Q 13 8 16 5" />
	</svg>
);

// ─── 主组件 ───

const Banner = ({
	topBadge,
	title,
	description,
	features = [],
	featureBoldColon = false,
	rightContentType = "upload",
	rightContent,
	rightImage,
	rightImageAlt = "Banner image",
	carouselImages = [],
	visibleCount = 3,
	uploadTitle,
	uploadSubtitle,
	uploadFormats,
	uploadSecure,
	fileSize,
	trustedBy,
	productionQuoteText,
	primaryButtonText,
	primaryButtonHref,
	stats = [],
	secondaryButtonText,
	carouselImageWidth,
	carouselImageHeight,
	carouselImageScales,
}: BannerProps) => {
	const { openModalInquiry } = useModalInquiryContext();
	const [carouselIndex, setCarouselIndex] = useState(0);

	const nextSlide = useCallback(() => {
		if (carouselImages.length === 0) return;
		setCarouselIndex((prev) => (prev + 1) % carouselImages.length);
	}, [carouselImages.length]);

	useEffect(() => {
		if (carouselImages.length <= visibleCount) return;
		const timer = setInterval(nextSlide, 3000);
		return () => clearInterval(timer);
	}, [nextSlide, carouselImages.length, visibleCount]);

	const visibleImages =
		carouselImages.length > 0
			? Array.from(
					{ length: Math.min(visibleCount, carouselImages.length) },
					(_, i) => (carouselIndex + i) % carouselImages.length,
				)
			: [];

	return (
		<>
			<section className="relative min-h-[400px] w-full overflow-hidden sm:min-h-[450px] md:min-h-[500px]">
				<div className="absolute inset-0 bg-[linear-gradient(180deg,_#ffffff_0%,_#ffffff_75%,_#eef6e5_90%,_#ecfed4_100%)]" />

				<div className="container relative z-10 px-4 pb-10 pt-8 font-sans sm:px-6 sm:pb-14 sm:pt-12 md:pb-16 md:pt-14 lg:pb-20 lg:pt-16">
					<div className="flex h-full w-full flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
						{/* 左侧内容区 */}
						<div className="max-w-[600px] pr-2 sm:pr-4 md:pr-6 lg:pr-8 xl:min-w-[600px]">
							<div className="-ml-[18px] inline-block">
								<div
									className="trustpilot-widget scale-125"
									data-locale="en-GB"
									data-template-id="5419b637fa0340045cd0c936"
									data-businessunit-id="68808142dd0a4c4d3e411b9c"
									data-style-height="24px"
									data-style-width="100%"
								>
									<a
										href="https://www.trustpilot.com/review/www.cncprotolabs.com?utm_medium=trustbox&utm_source=MicroTrustScore"
										target="_blank"
										rel="noopener"
									>
										Excellent Trustpilot
									</a>
								</div>
							</div>
							{/* {topBadge && <p className="mb-2 text-sm font-medium text-gray-900 sm:text-base">{topBadge}</p>} */}
							<h1 className="mb-3 text-[30px] font-extrabold leading-tight text-black sm:mb-4 sm:text-4xl md:text-[42px] lg:text-5xl">
								{title}
							</h1>

							{description && (
								<p className="mb-3 text-sm leading-relaxed text-black sm:mb-4 md:text-base">{description}</p>
							)}

							{features.length > 0 && (
								<ul className="mb-8 space-y-2 sm:mb-10 sm:space-y-2.5 md:mb-12 md:space-y-3">
									{features.map((feature, index) => {
										const colonIdx = featureBoldColon ? feature.indexOf(":") : -1;
										return (
											<li key={index} className="flex items-start gap-2 sm:gap-3">
												<CheckIcon />
												<span className="text-xs leading-relaxed text-black sm:text-sm md:text-base">
													{colonIdx > 0 ? (
														<>
															<strong>{feature.slice(0, colonIdx + 1)}</strong>
															{feature.slice(colonIdx + 1)}
														</>
													) : (
														feature
													)}
												</span>
											</li>
										);
									})}
								</ul>
							)}

							<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
								{primaryButtonText ? (
									primaryButtonHref?.startsWith("http") ? (
										<a
											href={primaryButtonHref}
											target="_blank"
											rel="noopener noreferrer"
											className="group inline-flex w-full items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
										>
											<CloudArrowUp className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]" weight="fill" />
											{primaryButtonText}
										</a>
									) : (
										<Link
											href={primaryButtonHref || "/upload"}
											className="group inline-flex w-full items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
										>
											<CloudArrowUp className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]" weight="fill" />
											{primaryButtonText}
										</Link>
									)
								) : (
									<GetInstantQuoteButton />
								)}
								<button
									type="button"
									onClick={openModalInquiry}
									className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
								>
									{secondaryButtonText || productionQuoteText}
								</button>
							</div>
						</div>

						{/* 右侧内容区 - 支持三种模式：upload、image、custom */}
						<div className="relative w-full flex-1 lg:max-w-[800px]">
							{/* 模式1：自定义内容 */}
							{rightContentType === "custom" && rightContent}

							{/* 模式2：单张图片 */}
							{rightContentType === "image" && rightImage && (
								<div className="relative mx-auto aspect-[4/2] w-full max-w-[600px] overflow-hidden rounded-xl">
									<Image
										src={rightImage}
										alt={rightImageAlt}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 100vw, 600px"
										priority
									/>
								</div>
							)}

							{/* 模式3：上传卡片（默认） */}
							{rightContentType === "upload" && (
								<div className="mx-auto max-w-[800px] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8">
									{/* 产品图片轮播 */}
									{visibleImages.length > 0 && (
										<div className="mb-3 flex items-center justify-center gap-4">
											{visibleImages.map((imgIndex) => {
												const scale = carouselImageScales?.[imgIndex] ?? 1;
												return (
													<div
														key={`carousel-${imgIndex}`}
														className={`relative overflow-hidden transition-opacity duration-500 ${
															!carouselImageWidth ? "w-[100px] md:w-[120px]" : ""
														} ${!carouselImageHeight ? "h-[100px] md:h-[120px]" : ""}`}
														style={{
															...(carouselImageWidth && { width: `${carouselImageWidth}px` }),
															...(carouselImageHeight && { height: `${carouselImageHeight}px` }),
														}}
													>
														<Image
															src={carouselImages[imgIndex]}
															alt={`Part ${imgIndex + 1}`}
															fill
															className="object-contain"
															style={{ transform: scale !== 1 ? `scale(${scale})` : undefined }}
															sizes={carouselImageWidth ? `${carouselImageWidth}px` : "120px"}
														/>
													</div>
												);
											})}
										</div>
									)}

									<p className="mb-4 text-center text-sm text-black md:text-base">{uploadTitle}</p>

									<Link href="/upload" className="block">
										<div className="rounded-lg border-2 border-dashed border-mainColor bg-[#f8fff7] p-5 transition-all duration-300 hover:border-mainColor hover:bg-[#f8fff7] md:p-6">
											<p className="mb-3 text-center text-sm text-black md:text-base">{uploadSubtitle}</p>
											<p className="mb-4 text-center text-[10px] text-gray-900 md:text-xs">{uploadFormats}</p>
											<div className="mb-3 flex justify-center">
												<UploadIcon className="h-12 w-12" />
											</div>
											{fileSize ? <p className="mb-2 text-center text-xs text-gray-900">{fileSize}</p> : null}
											<div className="flex items-center justify-center gap-1 text-xs text-gray-900">
												<LockSimple className="h-3 w-3" weight="fill" />
												<span>{uploadSecure}</span>
											</div>
										</div>
									</Link>

									{trustedBy ? (
										<p className="mt-5 text-center text-xs text-gray-900 md:text-sm">{trustedBy}</p>
									) : null}
								</div>
							)}
						</div>
					</div>
				</div>
			</section>
		</>
	);
};

export default Banner;
